import React, { useEffect, useState } from 'react';
import { useAuth } from '../../auth/hooks/useAuth';
import { usePortfolioView } from '../context/PortfolioViewContext';
import { useDashboardData } from '../hooks/useDashboardData';
import { usePrivateCacheRebuildStatus } from '../hooks/usePrivateCacheRebuildStatus';
import { DashboardProvider } from '../context/DashboardContext';
import { loadPortfolioPrefs, ViewTier, TimeWindow } from '../../../shared/utils/portfolioPrefs';

type Props = { children: React.ReactNode };

const PortfolioLayout: React.FC<Props> = ({ children }) => {
  const { session } = useAuth();
  const { selectedTimeWindow } = usePortfolioView();
  
  const [viewTier, setViewTier] = useState<ViewTier>(() => {
    const prefs = loadPortfolioPrefs();
    return prefs.selfViewTier;
  });
  
  // Listen for tier changes (both cross-tab and same-window)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'vester:portfolio:prefs') {
        const prefs = loadPortfolioPrefs();
        setViewTier((current) => (
          current === prefs.selfViewTier ? current : prefs.selfViewTier
        ));
      }
    };
    
    const handleCustomEvent = (event: CustomEvent) => {
      // Custom event fired from ViewPortfolioAsModal in same window
      const prefs = loadPortfolioPrefs();
      setViewTier((current) => (
        current === prefs.selfViewTier ? current : prefs.selfViewTier
      ));
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('portfolioPrefsChanged', handleCustomEvent as EventListener);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('portfolioPrefsChanged', handleCustomEvent as EventListener);
    };
  }, []);
  
  // Single dashboard fetch for entire page
  const dashboardData = useDashboardData({
    mode: 'self',
    tier: viewTier === 'full' ? 'private' : viewTier, // Map 'full' to 'private' for backward compat
    window: selectedTimeWindow.key as TimeWindow,
    enabled: !!session?.access_token && viewTier !== 'basic',
  });
  
  // Poll private cache rebuild status for toast synchronization
  const rebuildStatus = usePrivateCacheRebuildStatus();
  
  // Show toast when:
  // - isRebuilding === true (backend confirmed rebuild in progress), OR
  // - refresh_needed === true (dashboard indicates refresh is needed)
  // Hide toast when:
  // - isRebuilding === false AND refresh_needed === false (both conditions must be false)
  // 
  // Cases covered:
  // 1. Case B: Cache stale, DB fresh → refresh_needed: True ✅
  // 2. Case C: Cache stale, DB stale → refresh_needed: True ✅
  // 3. Case D: Cache missing, DB stale → refresh_needed: True ✅
  const isRebuilding = rebuildStatus.isRebuilding === true;
  const refreshNeeded = dashboardData.meta?.refresh_needed === true;
  const showToast = viewTier !== 'basic' && (isRebuilding || refreshNeeded);
  
  return (
    <>
      {/* Toast driven by Redis rebuild status (not just network fetching) */}
      {/* z-40 ensures toast appears below modals (which use z-[10000]) - visible after modal closes */}
      {showToast && (
        <div className="fixed top-20 left-4 z-40 flex items-center space-x-2 bg-white px-3 py-2 rounded-md shadow-md border">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
          <span className="text-sm text-gray-600">Refreshing portfolio data…</span>
        </div>
      )}
      <DashboardProvider value={dashboardData}>
        {children}
      </DashboardProvider>
    </>
  );
};

export default PortfolioLayout;
